<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <div class="mb-4">
            <a class="text-primary text-body-2" style="cursor: pointer;" @click="backToList">‹ {{ $t('labelOrder.label.backToList') }}</a>
          </div>
          <v-alert v-if="message" type="info" density="compact" closable class="mb-4" :text="message" @click:close="message = ''" />
          <v-alert v-if="errorMessage" type="error" density="compact" closable class="mb-4" :text="errorMessage" @click:close="errorMessage = ''" />

          <!-- 申請情報（詳細画面と同じ並び） -->
          <v-card variant="outlined" class="mb-4">
            <div class="d-flex flex-wrap">
              <div v-for="item in infoItems" :key="item.label" class="info-cell">
                <div class="text-caption text-medium-emphasis font-weight-bold mb-1">{{ item.label }}</div>
                <div :class="item.muted ? 'text-disabled' : 'font-weight-medium'">{{ item.value }}</div>
              </div>
            </div>
          </v-card>

          <!-- サマリ -->
          <v-row dense class="mb-2">
            <v-col v-for="c in summaryItems" :key="c.label" cols="3">
              <v-card variant="outlined" class="pa-3">
                <div class="text-caption text-medium-emphasis font-weight-bold">{{ c.label }}</div>
                <div class="text-h6" :class="c.color">{{ c.value }}</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- 一括反映 -->
          <v-card variant="outlined" class="pa-4 mb-4">
            <div class="d-flex align-center flex-wrap" style="gap: 12px;">
              <v-text-field
                v-model.number="bulkQty"
                type="number"
                min="0"
                step="500"
                :label="$t('labelOrder.label.bulkQty')"
                variant="outlined"
                density="compact"
                hide-details
                style="max-width: 180px;"
                class="text-right-input"
              />
              <v-btn variant="outlined" @click="bulkApply">{{ $t('labelOrder.action.bulkSelected') }}</v-btn>
              <v-divider vertical class="mx-1"></v-divider>
              <v-btn variant="outlined" @click="bulkApplyAll">{{ $t('labelOrder.action.bulkAll') }}</v-btn>
              <v-btn variant="text" @click="clearQty">{{ $t('labelOrder.action.clearQty') }}</v-btn>
              <v-spacer></v-spacer>
              <div class="text-caption text-medium-emphasis" style="max-width: 320px;">{{ $t('labelOrder.label.bulkHint') }}</div>
            </div>
          </v-card>

          <!-- 店舗ごとの発注枚数 -->
          <v-table density="compact" class="text-no-wrap">
            <thead>
              <tr>
                <th style="width: 48px;">
                  <v-checkbox-btn :model-value="allSelected" :indeterminate="someSelected && !allSelected" @update:model-value="toggleAll" />
                </th>
                <th>{{ $t('labelOrder.label.lineTable.siteCode') }}</th>
                <th>{{ $t('labelOrder.label.lineTable.storeName') }}</th>
                <th>{{ $t('labelOrder.label.lineTable.janCode') }}</th>
                <th class="text-right">{{ $t('labelOrder.label.lineTable.remaining') }}</th>
                <th>{{ $t('labelOrder.label.lineTable.state') }}</th>
                <th class="text-right" style="width: 160px;">{{ $t('labelOrder.label.lineTable.qty') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in stores" :key="s.siteCode" :class="s.selected ? 'bg-blue-lighten-5' : ''">
                <td><v-checkbox-btn v-model="s.selected" /></td>
                <td class="text-medium-emphasis" style="font-family: monospace;">{{ s.siteCode }}</td>
                <td class="font-weight-medium">{{ s.storeName }}</td>
                <td class="text-medium-emphasis" style="font-family: monospace;">{{ s.janCode }}</td>
                <td class="text-right font-weight-bold" :class="s.remainingQty < 0 ? 'text-red-darken-1' : ''">{{ num(s.remainingQty) }}</td>
                <td>
                  <v-chip size="x-small" label variant="tonal" :color="remainColor(s.remainingQty)">
                    {{ $t('labelStock.label.state.' + remainState(s.remainingQty)) }}
                  </v-chip>
                </td>
                <td class="text-right">
                  <v-text-field
                    :model-value="s.qty"
                    @update:model-value="setQty(s, $event)"
                    type="number"
                    min="0"
                    step="500"
                    placeholder="0"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="text-right-input"
                  />
                </td>
              </tr>
              <tr v-if="!isLoading && stores.length === 0">
                <td colspan="7" class="text-center text-medium-emphasis">
                  {{ $t('common.label.all') }} 0 {{ $t('common.label.totalCountOf') }}
                </td>
              </tr>
            </tbody>
          </v-table>
          <v-progress-linear v-if="isLoading" indeterminate color="indigo" />

          <!-- アクション -->
          <v-card variant="outlined" class="mt-4 pa-4">
            <div class="d-flex align-center">
              <span class="text-body-2 text-medium-emphasis">{{ $t('labelOrder.label.selectedStores') }}</span>
              <span class="text-h6 mx-2">{{ selectedLines.length }}</span>
              <span class="text-body-2 text-medium-emphasis">{{ $t('labelOrder.label.unitStore') }}</span>
              <v-divider vertical class="mx-4"></v-divider>
              <span class="text-body-2 text-medium-emphasis">{{ $t('labelOrder.label.total') }}</span>
              <span class="text-h6 text-blue-darken-2 mx-2">{{ num(selectedTotal) }}</span>
              <span class="text-body-2 text-medium-emphasis">{{ $t('labelOrder.label.unitSheet') }}</span>
              <v-spacer></v-spacer>
              <v-btn variant="text" class="mr-2" @click="backToList">{{ $t('labelOrder.action.back') }}</v-btn>
              <v-btn class="px-8 bg-indigo-darken-1" elevation="2" :disabled="selectedLines.length === 0" @click="openConfirm">
                {{ $t('labelOrder.action.submit') }}
              </v-btn>
            </div>
          </v-card>
        </v-container>

        <!-- 確認モーダル -->
        <v-dialog v-model="confirmDialog" width="640" persistent scrollable>
          <v-card>
            <v-card-title>{{ $t('labelOrder.dialog.confirmTitle') }}</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="text-body-2 mb-4">{{ $t('labelOrder.dialog.confirmText') }}</div>
              <v-alert v-if="submitError" type="error" density="compact" class="mb-4" :text="submitError" />
              <v-table density="compact" height="300" fixed-header>
                <thead>
                  <tr>
                    <th>{{ $t('labelOrder.label.lineTable.siteCode') }}</th>
                    <th>{{ $t('labelOrder.label.lineTable.storeName') }}</th>
                    <th class="text-right">{{ $t('labelOrder.label.lineTable.qty') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in selectedLines" :key="s.siteCode">
                    <td style="font-family: monospace;">{{ s.siteCode }}</td>
                    <td>{{ s.storeName }}</td>
                    <td class="text-right font-weight-bold">{{ num(s.qty) }}</td>
                  </tr>
                </tbody>
              </v-table>
              <v-row dense class="mt-3">
                <v-col cols="6">
                  <v-sheet class="pa-3 bg-grey-lighten-4 rounded">
                    <div class="text-caption text-medium-emphasis">{{ $t('labelOrder.label.summary.targetStores') }}</div>
                    <div class="text-h6">{{ selectedLines.length }} {{ $t('labelOrder.label.unitStore') }}</div>
                  </v-sheet>
                </v-col>
                <v-col cols="6">
                  <v-sheet class="pa-3 bg-blue-lighten-5 rounded">
                    <div class="text-caption text-medium-emphasis">{{ $t('labelOrder.label.summary.requestedTotal') }}</div>
                    <div class="text-h6 text-blue-darken-2">{{ num(selectedTotal) }} {{ $t('labelOrder.label.unitSheet') }}</div>
                  </v-sheet>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn variant="text" :disabled="isSubmitting" @click="confirmDialog = false">{{ $t('labelOrder.action.back') }}</v-btn>
              <v-btn class="px-6 bg-indigo-darken-1" elevation="2" :loading="isSubmitting" @click="submit">
                {{ $t('labelOrder.action.confirmSubmit') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import api from "@/apis/staff";
import router from "@/router/index";
import Paths from "@/router/indexPaths";
import store from "@/store/index";
import utils from "@/utils";
import { num, remainState, remainColor, REMAIN_WARN, setFlash } from "@/utils/labelManagement";

export default {
  components: { AppWrapperView },
  data: () => ({
    stores: [],
    bulkQty: 5000,
    isLoading: false,
    message: "",
    errorMessage: "",
    confirmDialog: false,
    isSubmitting: false,
    submitError: "",
  }),
  computed: {
    // 選択されていて枚数が1以上の店舗
    selectedLines() {
      return this.stores.filter((s) => s.selected && Number(s.qty) > 0);
    },
    selectedTotal() {
      return this.selectedLines.reduce((a, s) => a + Number(s.qty), 0);
    },
    allSelected() {
      return this.stores.length > 0 && this.stores.every((s) => s.selected);
    },
    someSelected() {
      return this.stores.some((s) => s.selected);
    },
    infoItems() {
      return [
        { label: this.$t("labelOrder.label.requestId"), value: this.$t("labelOrder.label.autoNumber"), muted: true },
        { label: this.$t("labelOrder.label.requestDate"), value: utils.getCalendarStr(new Date()) },
        { label: this.$t("labelOrder.label.requestUser"), value: store.getUserName() },
        { label: this.$t("labelOrder.label.status"), value: this.$t("labelOrder.label.notRequested"), muted: true },
        { label: this.$t("labelOrder.label.deliveryDate"), value: this.$t("labelOrder.label.setOnApprove"), muted: true },
        { label: this.$t("labelOrder.label.reviewer"), value: "—", muted: true },
      ];
    },
    summaryItems() {
      const entered = this.stores.filter((s) => Number(s.qty) > 0).length;
      const low = this.stores.filter((s) => s.remainingQty < REMAIN_WARN).length;
      const u = (k) => this.$t("labelOrder.label." + k);
      return [
        { label: this.$t("labelOrder.label.summary.targetStores"), value: `${this.selectedLines.length} ${u("unitStore")}`, color: "" },
        { label: this.$t("labelOrder.label.summary.requestedTotal"), value: `${num(this.selectedTotal)} ${u("unitSheet")}`, color: "text-blue-darken-2" },
        { label: this.$t("labelOrder.label.summary.notEntered"), value: `${this.stores.length - entered} ${u("unitStore")}`, color: "text-medium-emphasis" },
        { label: this.$t("labelOrder.label.summary.lowStock"), value: `${low} ${u("unitStore")}`, color: "text-amber-darken-3" },
      ];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    num,
    remainState,
    remainColor,
    load() {
      this.isLoading = true;
      api.post("/label-stock/search", {})
        .then((response) => {
          if (response.data?.status == 1) {
            this.errorMessage = response.data.errorMessage;
            return;
          }
          this.stores = (response.data.results?.stockList ?? []).map((s) => ({ ...s, selected: false, qty: "" }));
        })
        .catch(() => {
          this.stores = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // 枚数を入れた店舗は自動で選択状態にする
    setQty(s, v) {
      s.qty = v;
      if (Number(v) > 0) s.selected = true;
    },
    toggleAll(on) {
      this.stores.forEach((s) => (s.selected = !!on));
    },
    bulkApply() {
      const targets = this.stores.filter((s) => s.selected);
      if (targets.length === 0) {
        this.message = this.$t("labelOrder.message.noSelection");
        return;
      }
      targets.forEach((s) => (s.qty = this.bulkQty));
      this.message = this.$t("labelOrder.message.bulkApplied", [targets.length, num(this.bulkQty)]);
    },
    bulkApplyAll() {
      this.stores.forEach((s) => {
        s.selected = true;
        s.qty = this.bulkQty;
      });
      this.message = this.$t("labelOrder.message.bulkApplied", [this.stores.length, num(this.bulkQty)]);
    },
    clearQty() {
      this.stores.forEach((s) => (s.qty = ""));
    },
    openConfirm() {
      if (this.selectedLines.length === 0) {
        this.message = this.$t("labelOrder.message.noInput");
        return;
      }
      this.submitError = "";
      this.confirmDialog = true;
    },
    submit() {
      this.isSubmitting = true;
      this.submitError = "";
      const lines = this.selectedLines.map((s) => ({ siteCode: s.siteCode, qty: Number(s.qty) }));
      api.post("/label-order/register", { lines })
        .then((response) => {
          if (response.data?.status == 1) {
            this.submitError = response.data.errorMessage;
            return;
          }
          setFlash(this.$t("labelOrder.message.registered", [response.data.results.requestId]));
          this.confirmDialog = false;
          router.push({ name: Paths.LABEL_ORDER });
        })
        .catch(() => {
          this.submitError = this.$t("common.message.error");
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    backToList() {
      router.push({ name: Paths.LABEL_ORDER });
    },
  },
};
</script>

<style scoped>
.info-cell {
  flex: 1 1 160px;
  padding: 14px 18px;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
}
.text-right-input :deep(input) {
  text-align: right;
}
</style>
